import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { NotificationService } from '@core/services/notification.service';
import { AuthService } from '@features/auth/services/auth.service';
import {
  NotificationDtoJson,
  NotificationDtoJsonTypeEnum,
} from '@shared/models/api/models/NotificationDtoJson';
import { Observable } from 'rxjs';

@Component({
  selector: 'app-notification-list',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './notification-list.component.html',
  styleUrls: ['./notification-list.component.scss'],
})
export class NotificationListComponent implements OnInit {
  private readonly notificationService = inject(NotificationService);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  notifications$!: Observable<NotificationDtoJson[]>;

  constructor() {}

  ngOnInit() {
    this.notifications$ = this.notificationService.notifications$;

    // Debug: afficher le nombre de notifications
    this.notifications$.subscribe((notifications) => {
      console.log(`📬 Notifications count: ${notifications.length}`, notifications);
    });
  }

  /**
   * Fonction de tracking pour le @for
   */
  trackByAlertId(index: number, notification: NotificationDtoJson): number {
    return notification.alertId || index;
  }

  getIcon(type: NotificationDtoJsonTypeEnum): string {
    const icons: Record<NotificationDtoJsonTypeEnum, string> = {
      [NotificationDtoJsonTypeEnum.ASSIGNMENT_ACCEPTED]: '✅',
      [NotificationDtoJsonTypeEnum.MISSION_ASSIGNED_TO_DRIVER]: '🚚',
      [NotificationDtoJsonTypeEnum.ASSIGNMENT_REJECTED]: '❌',
      [NotificationDtoJsonTypeEnum.ASSIGNMENT_EXPIRED]: '⏰',
      [NotificationDtoJsonTypeEnum.MISSION_COMPLETED]: '✓',
      [NotificationDtoJsonTypeEnum.MISSION_CANCELLED]: '🚫',
      [NotificationDtoJsonTypeEnum.MISSION_DELAYED]: '⚠️',
      [NotificationDtoJsonTypeEnum.HIGH_PRIORITY_MISSION]: '🔥',
      [NotificationDtoJsonTypeEnum.DRIVER_AVAILABLE]: '👤',
      [NotificationDtoJsonTypeEnum.DRIVER_UNAVAILABLE]: '👤',
      [NotificationDtoJsonTypeEnum.SYSTEM]: '⚙️',
    };

    return icons[type] || '📬';
  }

  formatTimeAgo(timestamp: Date | string): string {
    const now = Date.now();

    // Convertir le timestamp en objet Date si c'est une string
    const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;

    // Vérifier que la date est valide
    if (Number.isNaN(date.getTime())) {
      console.error('Invalid timestamp:', timestamp);
      return 'Date invalide';
    }

    const seconds = Math.floor((now - date.getTime()) / 1000);

    if (seconds < 60) return "À l'instant";
    if (seconds < 3600) return `Il y a ${Math.floor(seconds / 60)} min`;
    if (seconds < 86400) return `Il y a ${Math.floor(seconds / 3600)} h`;
    return `Il y a ${Math.floor(seconds / 86400)} j`;
  }

  markAllAsRead(): void {
    const unreadNotifications = this.notificationService.getNotifications().filter((n) => !n.read);
    unreadNotifications.forEach((n) => {
      if (n.alertId) {
        this.notificationService.markAsRead(n.alertId);
      }
    });
  }

  /**
   * Vérifie si la navigation est disponible pour l'utilisateur actuel
   * Les drivers ont des notifications informatives seulement
   */
  isNavigationEnabled(): boolean {
    const currentUser = this.authService.currentUser();
    const userRole = currentUser?.role;

    // Navigation désactivée pour les drivers
    return userRole !== 'DRIVER';
  }

  onNotificationClick(notification: NotificationDtoJson): void {
    // Marquer comme lue
    if (!notification.read && notification.alertId) {
      this.notificationService.markAsRead(notification.alertId);
    }

    // Vérifier le rôle de l'utilisateur
    const currentUser = this.authService.currentUser();
    const userRole = currentUser?.role;

    console.log('User role:', userRole);

    // Pour les DRIVERS : notifications informatives seulement (pas de navigation)
    if (userRole === 'DRIVER') {
      console.log('Navigation disabled for drivers - notification is informative only');
      return;
    }

    // Pour les OPERATORS : navigation activée
    if (!notification.actionUrl) {
      console.log('No actionUrl provided in notification');
      return;
    }

    console.log('Original actionUrl:', notification.actionUrl);

    // Construire l'URL correcte pour l'opérateur
    let finalUrl = notification.actionUrl.trim();

    // Si l'URL commence par /missions/, ajouter le préfixe /operator
    if (finalUrl.startsWith('/missions/')) {
      finalUrl = `/operator${finalUrl}`;
    }
    // Si l'URL ne commence pas par /operator, l'ajouter
    else if (!finalUrl.startsWith('/operator/')) {
      finalUrl = `/operator${finalUrl.startsWith('/') ? '' : '/'}${finalUrl}`;
    }

    console.log('Final navigation URL:', finalUrl);

    // Naviguer vers l'URL construite
    const segments = finalUrl.split('/').filter(Boolean);
    console.log('Navigation segments:', segments);

    this.router.navigate(['/', ...segments]);
  }

  markAsRead(event: Event, notificationId: number): void {
    event.stopPropagation();
    this.notificationService.markAsRead(notificationId);
  }

  removeNotification(event: Event, notificationId: number): void {
    event.stopPropagation();
    this.notificationService.removeNotification(notificationId);
  }

  clearAll(): void {
    if (confirm('Voulez-vous vraiment effacer toutes les notifications ?')) {
      this.notificationService.clearAll();
    }
  }
}
